import React, { useEffect } from 'react'
import clsx from 'clsx'
import _toNumber from 'lodash/toNumber'
import { Dispatch, bindActionCreators } from 'redux'
import { useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import Skeleton from '@mui/material/Skeleton'
import StarIcon from '@mui/icons-material/Star'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { RootActionType, RootState } from 'duck'
import ButtonBase from 'components/Button/ButtonBase'
import Button from 'components/Button'
import Page from 'components/Page'
import Typography from 'components/Typography'
import ProjectSummary from './ProjectSummary'
import ProjectMine from './ProjectMine'
import {
  ID_PARAM,
  MineProjectSubRouteType,
  MINE_PROJECT_SUB_ROUTE_LIST,
  route,
  SUB_ROUTE_PARAM
} from 'routes'
import { apiActions, apiSelectors } from 'duck/ApiDuck'
import { defaultDescription } from 'appConstants'
import { useBreakpoint } from 'utils/Hooks'
import SEOHelmet from 'containers/AppPage/components/SEOHelmet'
import ShareOutlineIcon from 'components/Icon/ShareOutlineIcon'
import DialogShareUrl from './components/DialogShareUrl'
import { dialogActions, DialogNames } from 'duck/DialogDuck'
import BannerWelcome from 'components/Banner/BannerWelcome'
import { values } from 'appConstants'
import DialogProjectConfirmation from './components/DialogProjectConfirmation'
import ChipProjectLabel from 'components/Chip/ChipProjectLabel'

import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './ProjectPage.module.scss'

const mapStateToProps = (state: RootState) => ({
  openedMineProject: apiSelectors.openedMineProject(state),
  currentUserAddress: apiSelectors.currentUserAddress(state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      addDialog: dialogActions.addDialog,
      setOpenedProjectId: apiActions.mineProject.setOpenedProjectId
    },
    dispatch
  )

type ProjectPageProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>

const ProjectPage: React.FC<ProjectPageProps> = props => {
  const { openedMineProject, currentUserAddress, addDialog, setOpenedProjectId } = props

  const { isSmallscreenAbove } = useBreakpoint()
  const params = useParams()
  const subRouteRaw = params[SUB_ROUTE_PARAM] as MineProjectSubRouteType
  const subRoute = MINE_PROJECT_SUB_ROUTE_LIST.includes(subRouteRaw) ? subRouteRaw : undefined
  const id = _toNumber(params[ID_PARAM])
  const title = openedMineProject?.title || ''
  const user_full_name = openedMineProject?.user_full_name ?? ''
  const description = openedMineProject?.description || ''
  const isAdmin = currentUserAddress?.toLowerCase() === values.ADMIN_WALLET.toLowerCase()
  const type = openedMineProject?.type
  const typeText = type === 'artist' ? 'Artist' : type === 'collector' ? 'Collector' : 'Artist'

  useEffect(() => {
    setOpenedProjectId(id)
    return () => {
      setOpenedProjectId(undefined)
    }
  }, [id, setOpenedProjectId])

  const isInMineSubRoute = subRoute === 'mine' && openedMineProject?.type === 'collector'

  return (
    <Page>
      <SEOHelmet
        image={openedMineProject?.thumbnail?.file}
        title={`${title} | Art Mine`}
        description={`${description} - ${defaultDescription}`}
      />

      {isSmallscreenAbove ? <BannerWelcome /> : null}

      <div
        className={clsx(
          stylesGlobal.WidthFull,
          stylesGlobal.MarginXAuto,
          stylesGlobal.Container,
          stylesGlobal.PaddingBottom120,
          styles.Container
        )}
      >
        {subRoute !== 'mine' || (isSmallscreenAbove && subRoute === 'mine') ? (
          <>
            <ChipProjectLabel
              className={clsx(
                stylesGlobal.MarginBottom12,
                type === 'artist' && stylesGlobal.BgColorMagenta
              )}
              variant={!isSmallscreenAbove ? 'caption' : undefined}
            >
              {`${typeText} Curated`}
            </ChipProjectLabel>

            <div
              className={
                isSmallscreenAbove
                  ? clsx(
                      stylesGlobal.PaddingBottom24,
                      stylesGlobal.MarginBottom44,
                      stylesGlobal.BorderBottomPrimary
                    )
                  : stylesGlobal.MarginBottom32
              }
            >
              {openedMineProject ? (
                <>
                  {title ? (
                    <ButtonBase
                      to={route.MINE_PROJECTS.getUrl({
                        id: openedMineProject?.id ?? 0,
                        subRoute: 'summary'
                      })}
                      className={clsx(
                        stylesGlobal.JustifyContentFlexStart,
                        stylesGlobal.TextLeft,
                        stylesGlobal['Hover:Opacity50']
                      )}
                    >
                      <Typography variant="h3" variantXs="h5" component="h2" fontWeight="extraBold">
                        {`${title}`}
                      </Typography>
                    </ButtonBase>
                  ) : null}

                  <div className={clsx(stylesGlobal.DisplayFlex, stylesGlobal.AlignItemsFlexEnd)}>
                    <div className={clsx(stylesGlobal.FlexAuto, stylesGlobal.Width1)}>
                      {user_full_name ? (
                        <Typography
                          variant="body1"
                          variantXs="body2"
                          component="h3"
                          fontWeight="semibold"
                        >
                          {`by ${user_full_name}`}
                        </Typography>
                      ) : null}
                    </div>

                    <div
                      className={
                        isSmallscreenAbove
                          ? clsx(
                              stylesGlobal.DisplayFlex,
                              stylesGlobal.AlignItemsFlexEnd,
                              stylesGlobal.PaddingLeft16
                            )
                          : undefined
                      }
                    >
                      {isAdmin || openedMineProject.is_featured ? (
                        <div className={styles.AdminTools}>
                          <ButtonBase
                            className={clsx(stylesGlobal['-MarginBottom4'], stylesGlobal.PaddingY0)}
                            onClick={() => {
                              addDialog({
                                [DialogNames.PROJECT_CONFIRMATION]: {
                                  dialogName: DialogNames.PROJECT_CONFIRMATION,
                                  confirmType: 'set_featured_project'
                                }
                              })
                            }}
                          >
                            {openedMineProject.is_featured ? (
                              <StarIcon
                                className={stylesGlobal.FontSize24}
                                style={{ color: '#F9BD19' }}
                              />
                            ) : (
                              <StarOutlineIcon
                                style={{ color: '#323232' }}
                                className={stylesGlobal.FontSize24}
                              />
                            )}
                          </ButtonBase>

                          {isAdmin ? (
                            <ButtonBase
                              className={clsx(
                                stylesGlobal['-MarginBottom4'],
                                stylesGlobal.PaddingY0
                              )}
                              onClick={() => {
                                addDialog({
                                  [DialogNames.PROJECT_CONFIRMATION]: {
                                    dialogName: DialogNames.PROJECT_CONFIRMATION,
                                    confirmType: 'hide_project'
                                  }
                                })
                              }}
                            >
                              <DeleteOutlineIcon
                                style={{ color: '#323232' }}
                                className={stylesGlobal.FontSize24}
                              />
                            </ButtonBase>
                          ) : null}
                        </div>
                      ) : null}

                      <Button
                        startIcon={<ShareOutlineIcon className={stylesGlobal.FontSize24} />}
                        className={clsx(stylesGlobal['-MarginBottom4'], stylesGlobal.PaddingY0)}
                        size="small"
                        onClick={() => {
                          addDialog({
                            [DialogNames.PROJECT_COPYURL]: {
                              dialogName: DialogNames.PROJECT_COPYURL
                            }
                          })
                        }}
                      >
                        Share
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Typography variant="h3" variantXs="h5">
                    <Skeleton width="60%" />
                  </Typography>

                  <Typography variant="body1" variantXs="body2">
                    <Skeleton width="30%" />
                  </Typography>
                </>
              )}
            </div>
          </>
        ) : null}

        {!subRoute || subRoute === 'summary' || !isInMineSubRoute ? <ProjectSummary /> : null}
        {isInMineSubRoute ? <ProjectMine /> : null}
      </div>

      <DialogShareUrl />
      <DialogProjectConfirmation />
    </Page>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectPage)
